import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { assessmentsService } from '../api/assessmentsService';

const AssessmentInterviewGuides: React.FC = () => {
  const queryClient = useQueryClient();

  const guidesQuery = useQuery({
    queryKey: ['admin', 'interviewGuides'],
    queryFn: () => assessmentsService.listInterviewGuides(),
  });

  const rolesQuery = useQuery({
    queryKey: ['admin', 'assessmentRoles'],
    queryFn: () => assessmentsService.listRoleDefinitions(),
  });

  const [title, setTitle] = useState('');
  const [roleId, setRoleId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const guides = useMemo(() => guidesQuery.data?.data || [], [guidesQuery.data]);
  const interviewAreas = useMemo(
    () => (rolesQuery.data?.data || []).filter((role) => role.isInterviewArea),
    [rolesQuery.data],
  );
  const linkedRoleIds = useMemo(
    () => new Set(guides.map((guide) => guide.roleId).filter(Boolean) as number[]),
    [guides],
  );

  const onMutationError = (error: any) => {
    setErrorMessage(error?.response?.data?.message || 'Something went wrong.');
  };

  const invalidate = async () => {
    setErrorMessage(null);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'interviewGuides'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      assessmentsService.createInterviewGuide({
        title: title.trim(),
        roleId: roleId ? parseInt(roleId, 10) : null,
      }),
    onSuccess: async () => {
      setTitle('');
      setRoleId('');
      await invalidate();
    },
    onError: onMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { guideId: number; payload: any }) =>
      assessmentsService.updateInterviewGuide(vars.guideId, vars.payload),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: (guideId: number) => assessmentsService.deleteInterviewGuide(guideId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900">Interview Guides</h1>
          <p className="text-gray-600 mt-2">
            Discussion questions assessors see alongside their report. Link a guide to an interview area and
            every assessor working that area gets the questions plus space for optional notes.
          </p>
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Guide</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="border border-gray-300 rounded-md px-3 py-2 md:col-span-2"
              placeholder="Guide title (e.g. Worship Assessor Discussion Guide)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-md px-3 py-2"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
            >
              <option value="">Not linked to an area</option>
              {interviewAreas.map((role) => (
                <option key={role.id} value={role.id} disabled={linkedRoleIds.has(role.id)}>
                  {role.displayName}
                  {linkedRoleIds.has(role.id) ? ' (already has a guide)' : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            disabled={!title.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </button>
        </div>

        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Guides</h2>
          </div>
          <ResponsiveTableWrapper>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Guide</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Interview Area</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Questions</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Scorecard</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Active</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {guides.map((guide) => (
                  <tr key={guide.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/interview-guides/${guide.id}`}
                        className="text-sm font-medium text-primary-700 hover:text-primary-800 underline"
                      >
                        {guide.title}
                      </Link>
                      {guide.subtitle && <div className="text-xs text-gray-500">{guide.subtitle}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                        value={guide.roleId ? String(guide.roleId) : ''}
                        onChange={(e) =>
                          updateMutation.mutate({
                            guideId: guide.id,
                            payload: { roleId: e.target.value ? parseInt(e.target.value, 10) : null },
                          })
                        }
                      >
                        <option value="">Not linked to an area</option>
                        {interviewAreas.map((role) => (
                          <option
                            key={role.id}
                            value={role.id}
                            disabled={linkedRoleIds.has(role.id) && role.id !== guide.roleId}
                          >
                            {role.displayName}
                          </option>
                        ))}
                      </select>
                      {!guide.roleId && (
                        <div className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          Not linked to an area
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{guide.questionCount ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{guide.scorecardStatementCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={guide.isActive}
                        onChange={(e) =>
                          updateMutation.mutate({ guideId: guide.id, payload: { isActive: e.target.checked } })
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete "${guide.title}" and all of its questions?`)) {
                            deleteMutation.mutate(guide.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {guides.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-gray-600" colSpan={6}>
                      No interview guides yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ResponsiveTableWrapper>
        </div>
      </div>
    </Layout>
  );
};

export default AssessmentInterviewGuides;
